import {useState, useLayoutEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {RenderableDocument, DibJsonNode, ElementDataTags} from "../../types";
import classes from "./stikkord.module.css";

type DibObjectTag = {
    type: "tag";
    id: string;
    data: {
        id: string;
        name: string;
        tagtypeId: string;
        language: string;
    };
};

const hitsFirst = (tags: string[], incomingHitwords: string[] = []) => {
    const results = tags.reduce<{
        matchedCompletely: string[];
        matchedPartly: string[];
        rest: string[];
    }>(
        (accumulator, current) => {
            if (incomingHitwords.includes(current)) {
                return {
                    ...accumulator,
                    matchedCompletely: [
                        ...accumulator.matchedCompletely,
                        current,
                    ],
                };
            }
            if (
                incomingHitwords.some((hitword) => current.includes(hitword))
            ) {
                return {
                    ...accumulator,
                    matchedPartly: [...accumulator.matchedPartly, current],
                };
            }
            return {
                ...accumulator,
                rest: [...accumulator.rest, current],
            };
        },
        {
            matchedCompletely: [],
            matchedPartly: [],
            rest: [],
        }
    );
    return [
        ...results.matchedCompletely,
        ...results.matchedPartly,
        ...results.rest,
    ].join(", ");
};

export const Stikkord = ({
    node,
    topicData,
}: {
    augmentation: string;
    node: DibJsonNode;
    topicData: RenderableDocument;
}) => {
    const {t} = useTranslation();
    const [fullContentWidth, setFullContentWidth] = useState(0);
    const [buttonVisible, setButtonVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentWidth = useRef(0);

    const setWidth = () => {
        const parent = containerRef.current?.parentElement;
        if (parent) {
            setFullContentWidth(parent.getBoundingClientRect().width);
        }
        if (!contentWidth.current) {
            contentWidth.current = contentRef.current
                ? contentRef.current.getBoundingClientRect().width
                : 0;
        }
    };

    const contentOverflows = () => {
        if (containerRef.current && contentWidth.current) {
            const containerBox = containerRef.current.getBoundingClientRect();
            if (contentWidth.current > containerBox.width) {
                return true;
            }
        }
        return false;
    };

    useLayoutEffect(() => {
        if (!fullContentWidth) return;
        setButtonVisible(contentOverflows());
    }, [fullContentWidth]);

    useLayoutEffect(() => {
        setWidth();
    }, []);

    useLayoutEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        const debouncedSetWidth = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => setWidth(), 500);
        };
        window.addEventListener("resize", debouncedSetWidth);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener("resize", debouncedSetWidth);
        };
    }, []);

    const elementData = node.elementData as ElementDataTags | null;
    const tags = elementData?.tags ?? [];
    const tagNodes = tags
        .map((id) => topicData.objects[id] as DibObjectTag | undefined)
        .filter(Boolean) as DibObjectTag[];

    const sortedTags = hitsFirst(
        tagNodes.map((t) => t.data.name),
        []
    );

    const containerClassName = [
        classes.container,
        expanded && classes.containerExpanded,
    ]
        .filter(Boolean)
        .join(" ");

    const contentClassName = [
        classes.content,
        buttonVisible && !expanded && classes.contentOverflow,
        expanded && classes.contentExpanded,
    ]
        .filter(Boolean)
        .join(" ");

    const buttonClassName = [
        classes.showButton,
        expanded && classes.showButtonExpanded,
        !buttonVisible && !expanded && classes.showButtonHidden,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div
            className={containerClassName}
            ref={containerRef}
            style={{width: fullContentWidth ? `${fullContentWidth}px` : "100%"}}
        >
            <div className={contentClassName} ref={contentRef}>
                {t("content.tags", "Stikkord")} : {sortedTags}
            </div>
            <button
                className={buttonClassName}
                onClick={() => setExpanded(!expanded)}
            >
                {!expanded
                    ? t("action.seeMore", "Se flere")
                    : t("action.seeLess", "Se færre")}
            </button>
        </div>
    );
};
